'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ClipboardList } from 'lucide-react';
import { useProject } from '@/features/project/hooks/use-projects';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import { GoalsQualityIndicator } from '@/features/project/ui/GoalsQualityIndicator';
import { SettingsSectionRow } from './SettingsSection';
import {
  assistantTargets,
  elementTarget,
  sectionTarget,
} from '@/shared/assistant';

interface ProjectGoalsSectionProps {
  projectId: string;
  /// When provided, the parent owns the sheet open state. Lets sibling rows
  /// (e.g. the Prompts banner link) jump straight into the goals editor
  /// instead of bouncing the user out to the standalone goals page.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Render-settings row that opens the shared `ProjectGoalsSheet` (goals +
 * region + facade area editor). Mirrors Apple's `Project goals`
 * SettingsDestination and Android's `ProjectGoalsPanelContent`. The editor
 * itself (buffer semantics, autosave) lives in `ProjectGoalsSheet`, shared
 * with the estimate editor's facade-area link.
 */
export function ProjectGoalsSection({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSectionProps) {
  const t = useTranslations('projectGoals');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isSheetOpen = isControlled ? open : internalOpen;
  const setIsSheetOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  const { data: projectResponse } = useProject(projectId);
  const attrs = projectResponse?.data?.attributes;
  const hasGoals = (attrs?.goals ?? '').trim().length > 0;
  const score = attrs?.goals_quality_score ?? null;
  const pending = !!attrs?.is_goals_evaluation_pending;

  return (
    <>
      <SettingsSectionRow
        assistantTarget={assistantTargets(
          sectionTarget('brief'),
          elementTarget('brief_goals')
        )}
        icon={ClipboardList}
        title={t('label')}
        // Filled goals show the GSM quality indicator instead of a "Set" label —
        // the bars read as a status badge. Empty state keeps "Not set" + orange dot.
        subtitle={hasGoals ? undefined : t('notSet')}
        subtitleContent={
          hasGoals ? (
            <GoalsQualityIndicator
              score={score}
              hasGoals
              pending={pending}
              size="sm"
            />
          ) : undefined
        }
        // Orange attention dot ONLY when goals are empty — orange is reserved
        // for the empty/required-task indicator. Filled goals (even with
        // recommendations to read) show just the GSM quality signal.
        needsAttention={!hasGoals}
        onClick={() => setIsSheetOpen(true)}
      />

      <ProjectGoalsSheet
        projectId={projectId}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
      />
    </>
  );
}
